/* DARMA Set Builder engine — PP1 Phase C (Jul 2026; PROJECT_STATUS items 77–81).
   Round C1: pills / grid controls / tray (add, remove, size selects, tier walk,
   totals). Round C2 adds ?set= seeding; round C3 adds the cart wiring — module
   seams marked below. No Dawn dependencies; per-instance init on [data-dsb-root].

   DATA: everything comes from the section's server-rendered JSON island
   (variant ids, EXACT option values, availability at render, cents +
   money-formatted 100/80/70% strings, rounded AT RENDER). This file does NO
   money math on items — it sums the displayed per-item cents and formats the
   totals only. RECEIPT INVARIANT (item-79 rider): the displayed total = SUM of
   the displayed per-item rounded values, BY CONSTRUCTION (same cents summed).

   TIER COPY = ruled strings (S10 sweep list): "1 item. Full price." /
   "2 items. 20% off." / "N items. 30% off." at 3+ (R-C3 generalization).
   R-C3: 4+ items and duplicate items allowed (each add = one chip; C3
   aggregates equal variants into quantity for the cart POST).
   C4b (Jul 19, R9 AMENDED): grid cards carry a size select
   ([data-dsb-gridsize], server-rendered for the default colorway) — Add to
   Set passes its value into addItem; the dot handler refreshes its option
   availability IN PLACE on colorway change (node persists, C1.6 class);
   the TRAY select remains the place to change size after adding.
   R-C7: the pill bar's sticky offset is MEASURED from the live tabs bar
   (ResizeObserver -> --ds-tabs-h) — never a hardcoded constant.
   C1.2 (product-card canon, Jul 17): the dot handler also updates the card's
   [data-dsb-color] line (selected colorway value as stored, Title Case) —
   the ONLY engine change in that round; chip text was already the stored
   values (casing is CSS-side).
   C1.7 (tray totals rearrange, Jul 17 ruling): with items, the red Save
   line renders FIRST on the LEFT ([data-dsb-save], tier above it) and the
   totals render RIGHT as a stack ("Total" label > struck > final — house
   struck-above-final). renderTotals writes both zones; empty state
   unchanged (message + disabled CTA). Money values and the receipt
   invariant untouched — the same strings relocated.
   C1.7b (Derek's preview rulings, Jul 17): the amounts go INLINE under the
   Total label (struck LEFT of final, one baseline) — tray-only divergence
   from the stacked convention, recorded QA-rule-d d-10; the SAVE line
   becomes a BOX adopting the set-card chip treatment (CSS-side).
   C1.6 (mobile tray UX, Jul 17): (1) size change is SURGICAL — the <select>
   node persists across change (the old full re-render replaced it
   mid-interaction; on iOS the wheel picker fires change while open, so its
   commit hit a detached node = the double-tap bug; probe-proven
   sameNode=false on both engines). Only the chip's money + the totals
   re-render (chipPriceHtml/renderTotals split). The focus-restore hack is
   gone — focus never leaves the node. (2) every ADD scrolls the chips row
   so the new chip is in view (smooth; instant under prefers-reduced-motion;
   all widths). Seeding (C2) keeps the row start by ruling. On-device iOS
   re-check owed at Derek's preview — emulation masks picker semantics
   (item-56/57 class).
   C2 (?set= seeding, Jul 17 — tab contract rule 4, PROJECT_STATUS item 81):
   the seeding module below the tray handlers reads ?set= ONCE at init,
   resolves the key via [data-set-key] attribute compare on the same page,
   parses that card's data-ds-comp entries against the island, and REPLACES
   items[] (never appends) before the single initial renderTray(). LOAD-ONCE:
   nothing re-reads the URL, so the tabs bar's replaceState ?set= drop
   (contract rule 5) and later manual tab clicks can never un-seed or
   re-seed. The seeded chips row stays at scroll START (revealNewestChip is
   for manual adds only, C1.6 ruling). Every failure mode — no param,
   unknown key, bad JSON, unknown handle or colorway — is a SILENT skip to
   the normal empty state; no toast, no error (R-C9).
   C3 (cart wiring, Jul 17 — item-77d contract of record): the CTA is LIVE.
   ONE atomic POST routes.cart_add_url (JSON items[] with equal variants
   AGGREGATED into quantity, sections cart-drawer,cart-icon-bubble,
   sections_url). Gating (ratified C2 rider): empty tray OR any unavailable
   chip cannot POST (renderTotals sets disabled on n===0 || anyUnavail).
   422 is ATOMIC (zero partial adds): house error line above the trayrow
   (ink text + stock icon-error; NOT #C7302B — red stays Save-copy-only) +
   offending-chip mark (product-title match against the 422 description) +
   cartError publish. Success: cartUpdate publish (source darma-set-builder)
   + cart-drawer.renderContents(response) opens the drawer with the
   automatic discount already applied (R5: discounts + titles untouched) +
   the tray CLEARS. No-drawer guard -> routes.cart_url redirect. In-flight:
   aria-disabled + house spinner, re-entry guarded. Any tray state change
   clears the error + chip marks (renderTotals).
   p35-B (Sep 23 2026, Derek: "make it so that if you click any card, anywhere
   on the card, it adds that item to the set. I do not want it going to the
   PDP page"): the Add to Set BUTTON is gone and the card is the site's
   product card. The card's control is its overlay button [data-dsb-hit]
   (stretched over the card by CSS): a click / tap / Enter / Space on it adds
   the card's selection — the selected colorway (sel[]) and the grid size
   select's value — through the SAME addItem() as before. The dots
   ([data-dsb-dot], each a button around the grid card's 9px .darma-cg__dot)
   and the size select sit above the overlay as its SIBLINGS, so a dot click
   or a size change chooses and never adds (the grid handler returns before
   the add branch, and the overlay is never an ancestor of either). Arrow
   keys move along a card's dots and select. The [data-dsb-plink] links and
   their ?color= rewrite are retired (no PDP link in the builder). NEW:
   refreshCard() writes each card's state — "Added" / "Add to set" copy,
   aria-pressed on the overlay (true = the card's current colorway + size is
   in the tray), the sold-out line and the overlay's label — and runs from
   renderTotals(), i.e. on every tray change (add, remove, tray size change,
   seed, cart success). The tray, the tier line, the totals, the seeding and
   the cart POST are untouched. */

(function () {
  'use strict';

  var uid = 1;

  /* money format: money_without_trailing_zeros ($1,234; cents only when not
     whole, which the tray never shows: Oct 2 money rule); totals only. */
  function fmtMoney(cents) {
    var s = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
    var parts = s.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function init(root) {
    if (root.dataset.dsbInit) return;
    root.dataset.dsbInit = '1';

    var dataEl = root.querySelector('[data-dsb-data]');
    if (!dataEl) return;
    var catalog;
    try { catalog = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var products = {};
    (catalog.products || []).forEach(function (p) { products[p.handle] = p; });

    var grid = root.querySelector('[data-dsb-grid]');
    var pillbar = root.querySelector('[data-dsb-pillbar]');
    var tray = root.querySelector('[data-dsb-tray]');
    if (!grid || !tray) return;
    var chipsEl = tray.querySelector('[data-dsb-chips]');
    var emptyEl = tray.querySelector('[data-dsb-empty]');
    var tierEl = tray.querySelector('[data-dsb-tier]');
    var totalsEl = tray.querySelector('[data-dsb-totals]');
    var leftEl = tray.querySelector('[data-dsb-left]');   /* C1.7: tier + Save column */
    var saveEl = tray.querySelector('[data-dsb-save]');   /* C1.7: relocated Save line */
    var ctaEl = tray.querySelector('[data-dsb-cta]');
    var errEl = tray.querySelector('[data-dsb-error]');           /* C3 error line */
    var errTextEl = errEl ? errEl.querySelector('[data-dsb-error-text]') : null;

    /* C3: error state — shown only by a failed POST; ANY tray state change
       clears it (renderTotals) along with the offending-chip marks */
    function showError(msg) {
      if (!errEl) return;
      if (errTextEl) errTextEl.textContent = msg; else errEl.textContent = msg;
      errEl.hidden = false;
    }
    function clearError() {
      if (errEl) {
        errEl.hidden = true;
        if (errTextEl) errTextEl.textContent = '';
      }
      Array.prototype.slice.call(chipsEl.querySelectorAll('.darma-set-builder__chip--error')).forEach(function (ch) {
        ch.classList.remove('darma-set-builder__chip--error');
      });
    }

    /* ---- sticky offset (R-C7): measure the tabs bar, never a constant ---- */
    var tabsWrap = document.querySelector('.section-darma-sets-tabs');
    function setTabsH() {
      root.style.setProperty('--ds-tabs-h', (tabsWrap ? tabsWrap.offsetHeight : 0) + 'px');
    }
    setTabsH();
    if (tabsWrap && 'ResizeObserver' in window) {
      new ResizeObserver(setTabsH).observe(tabsWrap);
    } else {
      window.addEventListener('resize', setTabsH);
    }

    /* ---- island lookups ---- */
    function matches(v, c, s) {
      if (c != null && v.c !== c) return false;
      if (s != null && v.s !== s) return false;
      return true;
    }
    function resolveVariant(p, c, s) {
      for (var i = 0; i < p.variants.length; i++) {
        if (matches(p.variants[i], c, s)) return p.variants[i];
      }
      return null;
    }
    function colorwayAvailable(p, c) {
      return p.variants.some(function (v) { return matches(v, c, null) && v.a; });
    }
    function sizesOf(p, c) {
      /* unique size values in island (= store) order, with per-(colorway,size)
         availability — REAL values only, standing principle */
      var out = [], seen = {};
      p.variants.forEach(function (v) {
        if (c != null && v.c !== c) return;
        if (v.s == null || seen[v.s]) return;
        seen[v.s] = true;
        out.push({ s: v.s, a: p.variants.some(function (w) { return matches(w, c, v.s) && w.a; }) });
      });
      return out;
    }
    function firstAvailSize(p, c) {
      var sizes = sizesOf(p, c);
      for (var i = 0; i < sizes.length; i++) { if (sizes[i].a) return sizes[i].s; }
      return sizes.length ? sizes[0].s : null;
    }
    function colorwayHex(p, c) {
      for (var i = 0; i < p.colorways.length; i++) {
        if (p.colorways[i].v === c) return p.colorways[i].hex;
      }
      return '';
    }

    /* ---- card selection state (server default = the card's data-dsb-cw —
       p35-B: single-colorway items carry no dot, so the colorway rides on the
       card itself; the tray keeps showing their colorway as before) ---- */
    var sel = {};
    Array.prototype.slice.call(grid.querySelectorAll('[data-dsb-card]')).forEach(function (card) {
      var cw = card.getAttribute('data-dsb-cw');
      if (!cw) {
        var on = card.querySelector('[data-dsb-dot][aria-pressed="true"]');
        cw = on ? on.getAttribute('data-dsb-dot') : null;
      }
      sel[card.dataset.dsbCard] = cw || null;
    });

    /* ---- pills (R-C5) ---- */
    if (pillbar) {
      pillbar.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-dsb-pill]');
        if (!btn) return;
        var g = btn.getAttribute('data-dsb-pill');
        Array.prototype.slice.call(pillbar.querySelectorAll('[data-dsb-pill]')).forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-sel', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        Array.prototype.slice.call(grid.querySelectorAll('[data-dsb-card]')).forEach(function (card) {
          card.classList.toggle('darma-set-builder__card--off', card.dataset.dsbGroup !== g);
        });
      });
    }

    /* ---- grid: dots + the card (R-C4: metals are dots too — one idiom).
       p35-B: a dot chooses (and returns — never adds); the card's overlay
       button [data-dsb-hit] adds. The dots and the size select are siblings
       of the overlay, never inside it, so no click on them can reach it. ---- */
    grid.addEventListener('click', function (e) {
      var dot = e.target.closest('[data-dsb-dot]');
      if (dot) {
        /* a colorway choice is never an add: this branch returns, and the
           overlay is not an ancestor of the dot (nothing to stop) */
        if (dot.getAttribute('aria-disabled') === 'true') return;
        var card = dot.closest('[data-dsb-card]');
        var p = products[card.dataset.dsbCard];
        if (!p) return;
        var c = dot.getAttribute('data-dsb-dot');
        sel[p.handle] = c;
        Array.prototype.slice.call(card.querySelectorAll('[data-dsb-dot]')).forEach(function (b) {
          var on = b === dot;
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          var mark = b.querySelector('.darma-cg__dot');
          if (mark) mark.classList.toggle('is-on', on); /* the grid card's selected outline */
        });
        Array.prototype.slice.call(card.querySelectorAll('[data-dsb-img]')).forEach(function (img) {
          img.classList.toggle('is-on', img.getAttribute('data-dsb-img') === c);
        });
        var v = resolveVariant(p, c, null);
        var priceEl = card.querySelector('[data-dsb-price]');
        if (priceEl && v) priceEl.textContent = v.f;
        /* the colorway label under the dots shows the selected value as stored */
        var colorEl = card.querySelector('[data-dsb-color]');
        if (colorEl) colorEl.textContent = c;
        /* p35-B: the material line follows a metal (925 Sterling Silver /
           24K Gold Vermeil); for the other colorways it is the same line */
        var matEl = card.querySelector('[data-dsb-mat]');
        var mat = dot.getAttribute('data-dsb-dot-mat');
        if (matEl && mat) matEl.textContent = mat;
        /* C4b: refresh the grid size select for the new colorway IN PLACE —
           the node PERSISTS (C1.6 standing class: never rebuild a select);
           disabled states + '· sold out' labels recomputed from the island;
           options absent from this colorway's size set disable too; a
           now-unavailable selection falls to the first available size.
           (Today's catalog is colorway-symmetric — ChiRho/Aegis 12 out in
           BOTH metals — so this path is probe-exercised, not user-visible.) */
        var gsel = card.querySelector('[data-dsb-gridsize]');
        if (gsel && p.sizeOpt) {
          var szs = sizesOf(p, c);
          var byVal = {};
          szs.forEach(function (x) { byVal[x.s] = x; });
          Array.prototype.slice.call(gsel.options).forEach(function (o) {
            var en = byVal[o.value];
            var a2 = !!(en && en.a);
            o.disabled = !a2;
            o.textContent = o.value + (a2 ? '' : ' · sold out');
          });
          if (gsel.selectedIndex < 0 || gsel.options[gsel.selectedIndex].disabled) {
            var fas = firstAvailSize(p, c);
            if (fas != null) gsel.value = fas;
          }
        }
        refreshCard(card); /* availability, copy, aria-pressed, label */
        return;
      }
      var hit = e.target.closest('[data-dsb-hit]');
      if (hit) {
        if (hit.getAttribute('aria-disabled') === 'true') return; /* sold out */
        var card2 = hit.closest('[data-dsb-card]');
        var p2 = products[card2.dataset.dsbCard];
        if (!p2) return;
        var c2 = sel[p2.handle];
        /* C4b: the grid select's choice rides into the tray */
        var gs2 = card2.querySelector('[data-dsb-gridsize]');
        addItem(p2, c2, gs2 ? gs2.value : null);
      }
    });

    /* p35-B: a size change chooses, never adds — it only re-reads the card's
       state (is THIS size in the set?) */
    grid.addEventListener('change', function (e) {
      var gs = e.target.closest('[data-dsb-gridsize]');
      if (!gs) return;
      var card = gs.closest('[data-dsb-card]');
      if (card) refreshCard(card);
    });

    /* p35-B keyboard: Tab reaches the card (Enter / Space adds — native
       button) and then each dot (Space / Enter chooses); on a dot the arrow
       keys move along the card's dots and choose, skipping sold-out ones. */
    grid.addEventListener('keydown', function (e) {
      var dot = e.target.closest ? e.target.closest('[data-dsb-dot]') : null;
      if (!dot) return;
      var k = e.key;
      var dir = (k === 'ArrowRight' || k === 'ArrowDown') ? 1 : (k === 'ArrowLeft' || k === 'ArrowUp') ? -1 : 0;
      if (!dir) return;
      e.preventDefault();
      var dots = Array.prototype.slice.call(dot.parentNode.querySelectorAll('[data-dsb-dot]')).filter(function (d) {
        return d === dot || d.getAttribute('aria-disabled') !== 'true';
      });
      var i = dots.indexOf(dot);
      var next = dots[(i + dir + dots.length) % dots.length];
      if (next && next !== dot) { next.focus(); next.click(); }
    });

    /* p35-B: one card's state from the tray + its own selection. "Added" and
       aria-pressed = the card's CURRENT colorway + size resolves to a variant
       already in the tray (switch the colorway and the card offers it again;
       clicking an added card adds another — R-C3 duplicates stand, removal
       stays in the tray). */
    function refreshCard(card) {
      var p = products[card.dataset.dsbCard];
      if (!p) return;
      var c = sel[p.handle];
      var gs = card.querySelector('[data-dsb-gridsize]');
      var v = resolveVariant(p, c, gs ? gs.value : null);
      var avail = c != null ? colorwayAvailable(p, c) : p.variants.some(function (w) { return w.a; });
      var added = !!v && items.some(function (it) {
        if (it.handle !== p.handle) return false;
        var w = resolveVariant(p, it.c, it.s);
        return !!w && w.id === v.id;
      });
      card.classList.toggle('is-added', added);
      card.classList.toggle('is-out', !avail);
      var copy = card.querySelector('[data-dsb-addcopy]');
      if (copy) {
        copy.textContent = added ? 'Added' : 'Add to set';
        copy.hidden = !avail;
      }
      var so = card.querySelector('[data-dsb-soldout]');
      if (so) so.hidden = avail;
      var hitEl = card.querySelector('[data-dsb-hit]');
      if (hitEl) {
        hitEl.setAttribute('aria-pressed', added ? 'true' : 'false');
        if (avail) hitEl.removeAttribute('aria-disabled'); else hitEl.setAttribute('aria-disabled', 'true');
        var priceEl = card.querySelector('[data-dsb-price]');
        var hasDots = !!card.querySelector('[data-dsb-dot]');
        hitEl.setAttribute('aria-label',
          p.title + (hasDots && c ? ', ' + c : '') + (priceEl ? ', ' + priceEl.textContent : '') +
          ', ' + (!avail ? 'sold out' : added ? 'added' : 'add to set'));
      }
    }
    function refreshCards() {
      Array.prototype.slice.call(grid.querySelectorAll('[data-dsb-card]')).forEach(refreshCard);
    }

    /* ---- tray state ---- */
    var items = []; /* {uid, handle, c|null, s|null} — duplicates allowed (R-C3) */

    /* C4b (R9 amended): s = the grid select's chosen size when present;
       falls back to first-available (the seeding path passes no s and keeps
       the R9 default). The tray select remains the place to change it. */
    function addItem(p, c, s) {
      items.push({
        uid: uid++,
        handle: p.handle,
        c: c,
        s: p.sizeOpt ? (s != null ? s : firstAvailSize(p, c)) : null
      });
      renderTray();
      revealNewestChip(); /* C1.6 ruling: every add brings the new chip into view */
    }

    /* C1.6: the chips row scrolls the NEW chip into view on add (it appended
       off-screen right once the row overflowed — 978px past the edge at 390
       with 5 chips, measured). All widths; smooth unless the user prefers
       reduced motion (instant jump). Seeding (C2) keeps the row start. */
    function revealNewestChip() {
      if (!chipsEl.lastElementChild) return;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (typeof chipsEl.scrollTo === 'function') {
        chipsEl.scrollTo({ left: chipsEl.scrollWidth, behavior: reduce ? 'auto' : 'smooth' });
      } else {
        chipsEl.scrollLeft = chipsEl.scrollWidth;
      }
    }

    function tierCents(v, n) { return n >= 3 ? v.p7 : n === 2 ? v.p8 : v.p; }
    function tierCopy(n) {
      /* P1d amended strings (Jul 22 ruling — SUPERSEDES the C1.8b tier copy
         and the R-C3 generalization wording): 1 item = BLANK (deliberate —
         the "Add 1 more" nudge is removed by ruling), then all-caps short
         forms; >=3 carries the actual count. CSS renders caps/bold/ink. */
      if (n === 1) return '';
      if (n === 2) return '20% OFF 2';
      return '30% OFF ' + n;
    }

    function chipImgHtml(p, c) {
      /* clone the grid card's colorway img — same CDN URL the grid already
         loaded = zero extra download; sizes pinned to the 72px chip tile */
      var card = grid.querySelector('[data-dsb-card="' + p.handle + '"]');
      var src = null;
      if (card) {
        var imgs = Array.prototype.slice.call(card.querySelectorAll('[data-dsb-img]'));
        for (var i = 0; i < imgs.length; i++) {
          if (imgs[i].getAttribute('data-dsb-img') === (c || '')) { src = imgs[i]; break; }
        }
        if (!src && imgs.length) src = imgs[0];
      }
      if (!src) return '';
      return '<img src="' + esc(src.getAttribute('src')) + '"' +
        (src.getAttribute('srcset') ? ' srcset="' + esc(src.getAttribute('srcset')) + '"' : '') +
        ' sizes="72px" alt="" loading="lazy">';
    }

    function renderTray() {
      var n = items.length;
      if (!n) {
        chipsEl.hidden = true;
        chipsEl.innerHTML = '';
        emptyEl.hidden = false;
        /* C3b: FULL empty-state reset via renderTotals (hides the left zone,
           SAVE box and totals, clears tier/error, disables the CTA). The old
           manual reset here left the SAVE box + left zone VISIBLE beside the
           empty message whenever the tray emptied — latent since C1.7
           (remove-to-zero was never probed), surfaced by C3's success-clear
           and caught in the T1 drawer screenshot review. */
        renderTotals();
        return;
      }
      emptyEl.hidden = true;

      var strikeC = 0, finalC = 0, anyUnavail = false;
      var html = items.map(function (it) {
        var p = products[it.handle];
        var v = resolveVariant(p, it.c, it.s);
        if (!v) return '';
        strikeC += v.p;
        finalC += tierCents(v, n); /* receipt invariant: totals = Σ displayed per-item cents */
        if (!v.a) anyUnavail = true;

        var hex = it.c ? colorwayHex(p, it.c) : '';
        var sizeHtml = '';
        if (p.sizeOpt) {
          var opts = sizesOf(p, it.c).map(function (o) {
            return '<option value="' + esc(o.s) + '"' +
              (o.s === it.s ? ' selected' : '') + (o.a ? '' : ' disabled') + '>' +
              esc(o.s) + (o.a ? '' : ' · sold out') + '</option>';
          }).join('');
          sizeHtml = '<select class="darma-set-builder__chip-size" data-dsb-size="' + it.uid +
            '" aria-label="' + esc(p.sizeOpt) + '">' + opts + '</select>';
        }
        var priceHtml = chipPriceHtml(v, n);

        return '<div class="darma-set-builder__chip' + (v.a ? '' : ' darma-set-builder__chip--unavail') + '">' +
          '<span class="darma-set-builder__chip-tile"' +
            (hex ? ' style="border-bottom:3px solid ' + esc(hex) + '"' : '') + '>' +
            chipImgHtml(p, it.c) + '</span>' +
          '<span class="darma-set-builder__chip-info">' +
            '<span class="darma-set-builder__chip-name">' + esc(p.title) + '</span>' +
            (it.c ? '<span class="darma-set-builder__chip-var">' + esc(it.c) + '</span>' : '') +
            sizeHtml +
          '</span>' +
          '<span class="darma-set-builder__chip-price">' + priceHtml + '</span>' +
          '<button type="button" class="darma-set-builder__chip-x" data-dsb-remove="' + it.uid +
            '" aria-label="Remove ' + esc(p.title) + '">&#215;</button>' +
        '</div>';
      }).join('');

      var scrollKeep = chipsEl.scrollLeft;
      chipsEl.innerHTML = html;
      chipsEl.hidden = false;
      chipsEl.scrollLeft = scrollKeep;

      renderTotals();
    }

    function chipPriceHtml(v) {
      /* Oct 2 money rule: the chip shows the item's full price only (a
         per-item share would carry cents); the tray total carries the
         discount. Shared by the full render and the C1.6 size-change path. */
      return '<span class="darma-set-builder__chip-one">' + esc(v.f) + '</span>';
    }

    /* C1.6: tier + totals text only — no chip DOM rebuild. The receipt
       invariant holds by construction (same per-item cents summed).
       C1.7 arrangement (ruled): red Save line FIRST on the left (tier line
       above it), totals RIGHT as a stack — "Total" label above the struck
       original above the final (house struck-above-final convention). */
    function renderTotals() {
      var n = items.length;
      clearError(); /* C3: any tray change invalidates a prior cart error */
      if (!n) {
        tierEl.textContent = '';
        saveEl.textContent = '';
        saveEl.hidden = true;
        totalsEl.innerHTML = '';
        leftEl.hidden = true;
        totalsEl.hidden = true;
        ctaEl.disabled = true;
        refreshCards(); /* p35-B: every card back to "Add to set" */
        return;
      }
      var strikeC = 0, finalC = 0, anyUnavail = false;
      items.forEach(function (it) {
        var p = products[it.handle];
        var v = resolveVariant(p, it.c, it.s);
        if (!v) return;
        strikeC += v.p;
        finalC += tierCents(v, n); /* = the cart's set total, to the cent */
        if (!v.a) anyUnavail = true;
      });
      /* Oct 2 money rule: show the cart total rounded UP to the dollar (never
         under what the cart charges); Save = struck - shown, so they add up */
      finalC = Math.ceil(finalC / 100) * 100;
      leftEl.hidden = false;
      totalsEl.hidden = false;
      tierEl.textContent = tierCopy(n);
      saveEl.hidden = n < 2;
      saveEl.textContent = n >= 2 ? 'Save ' + fmtMoney(strikeC - finalC) : '';
      /* C1.7b: amounts INLINE under the Total label (struck left of final —
         a RULED tray-only divergence from the stacked set-card convention;
         QA-rule-d d-10, sweeps must not "fix" it) */
      totalsEl.innerHTML =
        '<span class="darma-set-builder__totals-label">Total</span>' +
        '<span class="darma-set-builder__totals-amounts">' +
        (n >= 2 ? '<s class="darma-set-builder__totals-strike">' + fmtMoney(strikeC) + '</s>' : '') +
        '<span class="darma-set-builder__totals-final">' + fmtMoney(finalC) + '</span>' +
        '</span>';

      /* C3 gate (ratified C2 rider): empty tray OR any unavailable chip
         (sold-out-size-mid-edit class) cannot POST */
      ctaEl.disabled = (n === 0 || anyUnavail);
      refreshCards(); /* p35-B: "Added" / aria-pressed follow the tray */
    }

    tray.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-dsb-remove]');
      if (rm) {
        var id = +rm.getAttribute('data-dsb-remove');
        items = items.filter(function (it) { return it.uid !== id; });
        renderTray();
      }
    });

    tray.addEventListener('change', function (e) {
      /* C1.6 SURGICAL SIZE CHANGE (double-tap fix, diagnosed + probe-proven):
         the old path re-rendered the whole tray on change, REPLACING the
         <select> mid-interaction (sameNode=false after one change event,
         both engines) — on iOS the wheel picker fires change while still
         open, so its commit landed on a detached node and the user's first
         tap died. The select now PERSISTS: state + this chip's money + the
         totals update in place; no rebuild, no refocus hack (focus never
         leaves the node). */
      var sz = e.target.closest('[data-dsb-size]');
      if (!sz) return;
      var id = +sz.getAttribute('data-dsb-size');
      var item = null;
      for (var i = 0; i < items.length; i++) {
        if (items[i].uid === id) { item = items[i]; break; }
      }
      if (!item) return;
      item.s = sz.value;
      var p = products[item.handle];
      var v = resolveVariant(p, item.c, item.s);
      var chip = sz.closest('.darma-set-builder__chip');
      if (chip && v) {
        chip.classList.toggle('darma-set-builder__chip--unavail', !v.a);
        var priceEl = chip.querySelector('.darma-set-builder__chip-price');
        if (priceEl) priceEl.innerHTML = chipPriceHtml(v, items.length);
      }
      renderTotals();
    });

    /* =====================================================================
       ROUND C2 — ?set= seeding (tab contract rule 4; header note above).
       Runs ONCE, here at init, before the single renderTray() below —
       REPLACE semantics by construction (items[] is assigned, never pushed).
       The tabs bar's replaceState ?set= drop happens on a later manual tab
       click and cannot un-seed; nothing here listens to URL changes, so a
       manual tab click never re-seeds. renderTray() preserves scrollLeft
       (0 at init) = the seeded chips row stays at scroll START
       (revealNewestChip is deliberately NOT called — manual adds only,
       C1.6 ruling). All failure modes are SILENT skips (R-C9: no toast):
       the builder just falls through to the normal empty state.
       ===================================================================== */
    (function seedFromSetParam() {
      var key = null;
      try { key = new URLSearchParams(window.location.search).get('set'); } catch (e) {}
      if (!key) return;
      /* resolve via [data-set-key] on the same page — attribute compare,
         no selector interpolation (arbitrary URL values are safe) */
      var comp = null;
      var cards = document.querySelectorAll('[data-set-key]');
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].getAttribute('data-set-key') === key) {
          comp = cards[i].getAttribute('data-ds-comp');
          break;
        }
      }
      if (!comp) return; /* unknown key (or a pre-C2 card): silent skip */
      var entries;
      try { entries = JSON.parse(comp); } catch (e) { return; }
      if (!Array.isArray(entries)) return;
      var seeded = [];
      entries.forEach(function (en) {
        if (!en || !en.h) return;
        var p = products[en.h];
        if (!p) return; /* unresolvable handle: silent skip */
        var c = en.c;
        if (c == null || c === 'default') {
          c = p.colorways.length ? p.colorways[0].v : null;
        }
        var s = p.sizeOpt ? firstAvailSize(p, c) : null; /* R9: sizes resolve in the tray */
        if (!resolveVariant(p, c, s)) return; /* unknown colorway: silent skip */
        seeded.push({ uid: uid++, handle: p.handle, c: c, s: s });
      });
      if (seeded.length) items = seeded; /* REPLACE, never append */
    })();

    /* =====================================================================
       ROUND C3 — cart wiring (item-77d contract; header note above).
       The POST body/headers mirror product-form.js's cart contract (JSON
       items[] variant of the same endpoint); publishes use the same
       PUB_SUB_EVENTS constants; drawer render + open = the house
       cart-drawer.renderContents path (drawer arrives with the automatic
       discount already applied — R5, zero discount mutations here).
       ===================================================================== */
    var busy = false;
    var cartEl = document.querySelector('cart-drawer');
    if (cartEl) ctaEl.setAttribute('aria-haspopup', 'dialog'); /* product-form parity */

    /* equal variants AGGREGATED into quantity (R-C3 duplicates ruling),
       first-seen order preserved */
    function cartPayload() {
      var out = [], idx = {};
      items.forEach(function (it) {
        var p = products[it.handle];
        var v = p ? resolveVariant(p, it.c, it.s) : null;
        if (!v) return;
        if (idx[v.id] != null) out[idx[v.id]].quantity += 1;
        else { idx[v.id] = out.length; out.push({ id: v.id, quantity: 1 }); }
      });
      return out;
    }

    /* offending-chip mark: the 422 description names the product — mark
       every tray chip whose title appears in it (cleared on any tray change) */
    function markOffenders(desc) {
      if (!desc) return;
      var chips = chipsEl.querySelectorAll('.darma-set-builder__chip');
      items.forEach(function (it, i) {
        var p = products[it.handle];
        if (p && chips[i] && desc.indexOf(p.title) !== -1) {
          chips[i].classList.add('darma-set-builder__chip--error');
        }
      });
    }

    function canPublish() {
      return typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
    }

    ctaEl.addEventListener('click', function () {
      if (busy || ctaEl.disabled || ctaEl.getAttribute('aria-disabled') === 'true') return;
      var payload = cartPayload();
      if (!payload.length) return;
      clearError();
      busy = true;
      ctaEl.setAttribute('aria-disabled', 'true');
      ctaEl.classList.add('is-loading');
      var spin = ctaEl.querySelector('.loading__spinner');
      if (spin) spin.classList.remove('hidden');

      var sections = (cartEl && cartEl.getSectionsToRender)
        ? cartEl.getSectionsToRender().map(function (s) { return s.id; })
        : ['cart-drawer', 'cart-icon-bubble'];
      var addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add.js';

      fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: payload,
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) {
            /* 422 — the multi-item add is ATOMIC: nothing was added (77d) */
            var desc = res.description || res.message || (window.cartStrings && window.cartStrings.error) || 'Unable to add the set to the cart.';
            showError(desc);
            markOffenders(res.description || '');
            if (canPublish()) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'darma-set-builder',
                errors: res.errors || res.description,
                message: res.message
              });
            }
            return;
          }
          if (canPublish()) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'darma-set-builder', cartData: res });
          }
          if (!cartEl || typeof cartEl.renderContents !== 'function') {
            window.location = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
            return;
          }
          cartEl.setActiveElement(document.activeElement);
          cartEl.renderContents(res); /* drawer opens; discount already applied */
          if (cartEl.classList.contains('is-empty')) cartEl.classList.remove('is-empty');
          items = [];
          renderTray(); /* contract: the tray clears on success */
        })
        .catch(function () {
          showError((window.cartStrings && window.cartStrings.error) || 'Unable to add the set to the cart. Please try again.');
        })
        .finally(function () {
          busy = false;
          ctaEl.removeAttribute('aria-disabled');
          ctaEl.classList.remove('is-loading');
          if (spin) spin.classList.add('hidden');
        });
    });

    renderTray();
  }

  function initAll() {
    Array.prototype.slice.call(document.querySelectorAll('[data-dsb-root]')).forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target && e.target.querySelector ? e.target.querySelector('[data-dsb-root]') : null;
    if (r) init(r);
  });
})();
